/**
 * @file useStartMeetingNotes.ts
 * @brief Start a Meeting Notes recording straight into the recorder: the
 *        Notes page's slide and Home's "Start notes" (Coming up).
 *
 * @details
 * Moved out of NotesPage (DES 07-10-2026, the phone's 7f996d8: one slide
 * starts Meeting Notes) so Home's calendar rows start the same way
 * (DES 19-10-2026, calm Home). Notes needs nothing else from Setup: the notes
 * language last chosen there, the default microphone, the desktop app's call
 * audio choice. The caller shows the recording notice first (GDPR Art 13) and
 * says how: `setup_slide` (the notice under a slide) or `scheduled_confirm`
 * (a confirm dialog, as a scheduled meeting's start).
 *
 * A calendar start fills the title and the attendees. Attendees only name the
 * voices: a Notes meeting is shared with no one (`shared: false`), as Setup
 * does in Notes mode (DES 10-10-2026).
 *
 * Related: ./NotesPage.tsx, ../home/ComingUpSection.tsx,
 *          ../employee-boardroom/BoardroomRecorderPage.tsx
 *
 * @author DES
 * @date 20-10-2026
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/shared/auth/auth-context";
import { useDesktopSettings } from "@/shared/desktop/useDesktopSettings";
import { toast } from "@/shared/hooks/useToast";
import type { BoardroomDraftParticipant } from "@/features/employee-boardroom/capture/boardroomDraftStore";
import { probeDurableStorage } from "@/features/employee-boardroom/capture/durabilityProbe";
import { describeRecorderSupportError } from "@/features/employee-boardroom/capture/micErrors";
import { canCaptureSystemAudio } from "@/features/employee-boardroom/capture/systemAudio";
import type { CalendarAttendee } from "@/features/employee-boardroom/calendarMeetings";
import { loadNotesLanguage } from "./notesLanguage";

export type StartMeetingNotesInput = {
  title?: string;
  attendees?: CalendarAttendee[];
  noticeMethod: "setup_slide" | "scheduled_confirm";
};

export function useStartMeetingNotes(boardroomBase: string) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const desktopSettings = useDesktopSettings();
  const [starting, setStarting] = useState(false);

  const start = async ({ title = "", attendees = [], noticeMethod }: StartMeetingNotesInput) => {
    const supportErr = describeRecorderSupportError();
    if (supportErr) {
      toast({ variant: "destructive", title: "Recording not supported", description: supportErr });
      return;
    }
    setStarting(true);
    try {
      const durability = await probeDurableStorage();
      if (!durability.ok) {
        toast({
          variant: "destructive",
          title: "Draft storage unavailable",
          description: durability.error,
        });
        return;
      }
      const selfEmail = (user?.email || "").toLowerCase();
      const participants: BoardroomDraftParticipant[] = attendees
        .filter((a) => a.email.toLowerCase() !== selfEmail)
        .map((a) => ({ name: a.name, email: a.email, isExternal: true, shared: false }));
      if (participants.length > 0 && user) {
        participants.unshift({
          id: user.id,
          name: user.name || user.email,
          email: user.email,
          isExternal: false,
          shared: false,
          profileImageUrl: user.profileImageUrl ?? undefined,
        });
      }
      navigate(`${boardroomBase}/meetings/record`, {
        state: {
          title: title.trim(),
          targetLanguages: [loadNotesLanguage()],
          participants,
          // Desktop app: the call audio choice from Settings, on by default
          // as on Setup.
          includeSystemAudio: canCaptureSystemAudio() && (desktopSettings?.callAudio ?? true),
          tier: "notes",
          recordingNotice: { method: noticeMethod, at: new Date().toISOString() },
        },
      });
    } finally {
      setStarting(false);
    }
  };

  return { start, starting };
}
